"use client";

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  ReactNode,
  useEffect,
  useMemo,
} from "react";
import { toast } from "sonner";
import { graphqlFetch } from "@/lib/graphql-client";
import { DELETE_APPOINTMENT } from "@/lib/apollo/queries/booking-edge";
import {
  BookingContextType,
  BookingService,
  GroupedServices,
  BookingPackage,
  Salon,
  SalonStaff,
  Client,
  AppointmentPreview,
  PackageVoucher,
  ClientMilestone,
  VoucherService,
  Appointment,
  Service,
  ServiceVariant,
  Package,
  Promo,
  Business,
  BookingStep,
  BookingTabName,
  WorkflowForm,
  WorkflowFormAnswers,
  GiftCardCartItem,
} from "@/types/booking";

const BookingContext = createContext<BookingContextType | undefined>(undefined);

export function useBooking() {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error("useBooking must be used within BookingProvider");
  }
  return context;
}

interface BookingProviderProps {
  children: ReactNode;
  businessInfo: Business | null;
  salons: Salon[];
  services: Service[];
  packages: Package[];
  faqs?: any[];
  reviews?: any[];
  promos?: Promo[];
  initialSelectedSalonId?: string | null;
  servicesLoading?: boolean;
}

export function BookingProvider({
  children,
  businessInfo: initialBusinessInfo,
  salons: initialSalons,
  services: initialServices,
  packages: initialPackages,
  faqs = [],
  reviews = [],
  promos = [],
  initialSelectedSalonId = null,
  servicesLoading: externalServicesLoading = false,
}: BookingProviderProps) {
  // Core State
  const [step, setStep] = useState(BookingStep.INITIAL);
  const [businessInfo] = useState<Business | null>(initialBusinessInfo);
  const [salons, setSalons] = useState<Salon[]>(initialSalons);
  const [selectedSalonId, setSelectedSalonId] = useState<string | null>(
    initialSelectedSalonId ||
      (initialSalons.length === 1 ? initialSalons[0]?.id : null)
  );
  
  // Sync selectedSalonId when initialSelectedSalonId prop changes
  // This handles the case where the wrapper sets the salon ID after initial render
  useEffect(() => {
    if (initialSelectedSalonId && initialSelectedSalonId !== selectedSalonId) {
      setSelectedSalonId(initialSelectedSalonId);
    }
  }, [initialSelectedSalonId]);

  // Update salons when initialSalons prop changes (for progressive loading of salon details)
  useEffect(() => {
    if (initialSalons.length > 0) {
      setSalons(initialSalons);
    }
  }, [initialSalons]);
  
  // Derive selectedSalon from salons array and selectedSalonId
  const selectedSalon = useMemo(() => {
    if (!selectedSalonId) return null;
    const salon = salons.find((s) => s.id === selectedSalonId) || null;
    return salon;
  }, [salons, selectedSalonId]);

  // Services State - use initialServices directly since it updates from props
  const [services, setServices] = useState<Service[]>(initialServices);
  const [locationType, setLocationType] = useState<string | null>(null);
  const [formattedServices, setFormattedServices] = useState<GroupedServices[]>(
    []
  );
  const [selectedServices, setSelectedServices] = useState<BookingService[]>(
    []
  );
  // Use external loading state passed from BookingPageWrapper
  const loadingServices = externalServicesLoading;

  // Packages State - use initialPackages directly since it updates from props
  const [packages, setPackages] = useState<Package[]>(initialPackages);
  
  // Update services when initialServices prop changes (for progressive loading)
  useEffect(() => {
    if (initialServices.length > 0) {
      setServices(initialServices);
    }
  }, [initialServices]);

  // Update packages when initialPackages prop changes (for progressive loading)
  useEffect(() => {
    if (initialPackages.length > 0) {
      setPackages(initialPackages);
    }
  }, [initialPackages]);
  const [formattedPackages, setFormattedPackages] = useState<BookingPackage[]>(
    []
  );
  const [selectedPackages, setSelectedPackages] = useState<BookingPackage[]>(
    []
  );
  const [giftCardCart, setGiftCardCartState] = useState<GiftCardCartItem | null>(
    null
  );

  // Staff State
  const [selectedStaff, setSelectedStaff] = useState<SalonStaff | null>(null);
  const [staffOption, setStaffOption] = useState<string | null>(null);
  const [availableServiceStaffs, setAvailableServiceStaffs] = useState<
    SalonStaff[]
  >([]);

  // Client State
  const [serviceClient, setServiceClient] = useState<Client | null>(null);

  // Appointment State
  const [appointmentDateTime, setAppointmentDateTime] = useState<string | null>(
    null
  );
  const [priceSummary, setPriceSummary] = useState<AppointmentPreview | null>(
    null
  );
  const [packagesPriceSummary, setPackagesPriceSummary] = useState<any | null>(
    null
  );
  const [bookedAppointment, setBookedAppointment] =
    useState<Appointment | null>(null);
  const [existingAppointment, setExistingAppointment] =
    useState<Appointment | null>(null);
  const [appointmentPaymentType, setAppointmentPaymentType] = useState<
    string | null
  >(null);
  const [actualAmountPaid, setActualAmountPaid] = useState<number | null>(null);
  const [actualProcessingFee, setActualProcessingFee] = useState<number | null>(null);

  // Voucher State
  const [voucherToBeRedeemed, setVoucherToBeRedeemed] =
    useState<PackageVoucher | null>(null);

  const [openVoucherRedemptionModal, setOpenVoucherRedemptionModal] =
    useState(false);

  // Selected time slot state (for availability polling - double-booking prevention)
  const [selectedDate, setSelectedDate] = useState<string | null>(null); // "YYYY-MM-DD"
  const [selectedTime, setSelectedTime] = useState<string | null>(null); // "HH:MM"

  // Slot reservation timer state (5-minute countdown from slot selection)
  const [slotSelectionTime, setSlotSelectionTime] = useState<number | null>(null);

  // Promo/Rewards State
  const [promoCodeApplication, setPromoCodeApplication] = useState<any | null>(
    null
  );
  const [promoCode, setPromoCode] = useState<string | null>(null);
  const [rewards, setRewards] = useState<ClientMilestone[]>([]);
  const [selectedReward, setSelectedReward] = useState<ClientMilestone | null>(
    null
  );
  const [rewardSelected, setRewardSelected] = useState(false);

  // UI State
  const [activeTab, setActiveTab] = useState(BookingTabName.SERVICES);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [searchCount, setSearchCount] = useState(0);
  const [searchWrapperIsOpen, setSearchWrapperIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Workflow Form State
  const [workflowForm, setWorkflowForm] = useState<WorkflowForm | null>(null);
  const [workflowFormAnswers, setWorkflowFormAnswers] = useState<WorkflowFormAnswers>({});
  const [showWorkflowFormModal, setShowWorkflowFormModal] = useState(false);
  const [workflowFormSubmissionId, setWorkflowFormSubmissionId] = useState<string | null>(null);

  // Refs
  const modalContentRef = useRef<HTMLDivElement | null>(null);
  const bookedAppointmentRef = useRef<Appointment | null>(null);
  const selectedSalonIdRef = useRef<string | null>(selectedSalonId);

  // Keep refs in sync with state
  useEffect(() => {
    bookedAppointmentRef.current = bookedAppointment;
  }, [bookedAppointment]);

  useEffect(() => {
    selectedSalonIdRef.current = selectedSalonId;
  }, [selectedSalonId]);

  // Toast function using Sonner
  const addToast = useCallback(
    (toastData: {
      message: string;
      variant: "success" | "error" | "warning";
    }) => {
      switch (toastData.variant) {
        case "success":
          toast.success(toastData.message);
          break;
        case "error":
          toast.error(toastData.message);
          break;
        case "warning":
          toast.warning(toastData.message);
          break;
        default:
          toast(toastData.message);
      }
    },
    []
  );

  const setGiftCardCart = useCallback(
    (giftCard: GiftCardCartItem | null) => {
      if (giftCard && (selectedServices.length > 0 || selectedPackages.length > 0)) {
        addToast({
          message: "Please clear your cart before adding a gift card.",
          variant: "error",
        });
        return;
      }
      setGiftCardCartState(giftCard);
    },
    [addToast, selectedPackages.length, selectedServices.length]
  );

  const clearGiftCardCart = useCallback(() => {
    setGiftCardCartState(null);
  }, []);

  const updateGiftCardCartQuantity = useCallback((quantity: number) => {
    setGiftCardCartState((prev) => {
      if (!prev) return prev;
      const safeQuantity = Math.max(1, Math.floor(quantity || 1));
      return { ...prev, quantity: safeQuantity };
    });
  }, []);

  // Check if mobile on mount
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Format services into groups - re-run when services change
  useEffect(() => {
    if (services.length > 0) {
      const grouped = formatServicesIntoGroups(services);
      setFormattedServices(grouped);
    }
  }, [services]);

  // Format vouchers (only packages that are purchasable as vouchers)
  useEffect(() => {
    if (packages.length > 0) {
      const formatted = packages.map((pkg) => ({
        ...pkg,
        quantity: 0,
        selected: false,
        cartQuantity: 0,
      }));

      setFormattedPackages(formatted);
    } else {
      setFormattedPackages([]);
    }
  }, [packages]);

  // Helper function to format services
  const formatServicesIntoGroups = (services: Service[]): GroupedServices[] => {
    const groups: { [key: string]: GroupedServices } = {};

    services.forEach((service) => {
      const categoryName = service.category?.name || "Other";
      const categoryKey = service.category?.id || "other";

      if (!groups[categoryKey]) {
        groups[categoryKey] = {
          name: categoryName,
          category: categoryKey,
          show: true,
          services: [],
        };
      }
      groups[categoryKey].services.push({
        ...service,
        selected: false,
        quantity: 1,
      });
    });

    return Object.values(groups).sort((a, b) => {
      // Sort by orderIndex if available, otherwise alphabetically
      const aOrder =
        services.find((s) => s.category?.id === a.category)?.category
          ?.orderIndex ?? 999;
      const bOrder =
        services.find((s) => s.category?.id === b.category)?.category
          ?.orderIndex ?? 999;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.name.localeCompare(b.name);
    });
  };

  // Navigation

  // Delete a held appointment to release the reserved time slot
  const deleteHeldAppointment = useCallback(
    async (appointment: Appointment, salonId: string) => {
      try {
        await graphqlFetch(DELETE_APPOINTMENT, {
          salonId,
          id: appointment.id,
        });
      } catch (error) {
        // Fire and forget - don't block navigation if deletion fails.
        // The slot will be released automatically when reserved_until expires.
        console.error("Failed to delete held appointment:", error);
      }
      // Clear the localStorage backup record (mirrors Payment.tsx's HELD_APPOINTMENT_KEY)
      try {
        localStorage.removeItem("splice_held_appointment");
      } catch {
        // Ignore — localStorage may be unavailable
      }
      setBookedAppointment(null);
    },
    []
  );

  const handleNextStep = useCallback((page?: number) => {
    // If navigating away from the Payment step while an appointment has been
    // created (but payment hasn't completed), delete the appointment to
    // release the held time slot immediately.
    setStep((prev) => {
      const targetStep = page !== undefined ? page : prev + 1;
      if (prev === BookingStep.PAYMENT && targetStep !== BookingStep.SUCCESS) {
        if (bookedAppointmentRef.current && selectedSalonIdRef.current) {
          deleteHeldAppointment(
            bookedAppointmentRef.current,
            selectedSalonIdRef.current
          );
        }
      }
      return targetStep;
    });
    scrollToTop();
  }, [deleteHeldAppointment]);

  const scrollToTop = useCallback(() => {
    if (modalContentRef.current) {
      modalContentRef.current.scrollTo({
        top: 0,
        behavior: "instant",
      });
    } else {
      window.scrollTo({
        top: 0,
        behavior: "instant",
      });
    }
  }, []);

  // Service Management
  const showOrHideCategory = useCallback(
    (categoryName: string, show?: boolean) => {
      setFormattedServices((prev) =>
        prev.map((category) =>
          category.name === categoryName
            ? { ...category, show: show !== undefined ? show : !category.show }
            : category
        )
      );
    },
    []
  );

  const selectService = useCallback(
    (serviceId: string) => {
      if (giftCardCart) {
        addToast({
          message: "Please clear gift cards from cart before adding services",
          variant: "error",
        });
        return;
      }
      if (selectedPackages.length > 0) {
        addToast({
          message: "Please clear packages from cart before adding services",
          variant: "error",
        });
        return;
      }

      // Update selected services
      setSelectedServices((prev) => {
        const exists = prev.find((s) => s.id === serviceId);

        if (exists) {
          return prev.filter((s) => s.id !== serviceId);
        } else {
          const service = services.find((s) => s.id === serviceId);

          if (service) {
            return [
              ...prev,
              { ...service, selected: true, quantity: 1, cartQuantity: 1 },
            ];
          } else {
            return prev;
          }
        }
      });

      // Update formatted services
      setFormattedServices((prev) =>
        prev.map((category) => ({
          ...category,
          services: category.services.map((service) =>
            service.id === serviceId
              ? { ...service, selected: !service.selected }
              : service
          ),
        }))
      );
    },
    [giftCardCart, selectedPackages, services, addToast]
  );

  const selectServiceWithVariant = useCallback(
    (serviceId: string, variant?: ServiceVariant | null) => {
      if (giftCardCart) {
        addToast({
          message: "Please clear gift cards from cart before adding services",
          variant: "error",
        });
        return;
      }
      if (selectedPackages.length > 0) {
        addToast({
          message: "Please clear packages from cart before adding services",
          variant: "error",
        });
        return;
      }

      // Update selected services
      setSelectedServices((prev) => {
        const exists = prev.find((s) => s.id === serviceId);

        if (exists) {
          return prev.filter((s) => s.id !== serviceId);
        } else {
          // Handle unique service IDs for variants (format: "serviceId-variantId")
          // Extract original service ID by removing the variant ID suffix
          let originalServiceId = serviceId;
          if (serviceId.includes("-") && variant) {
            // If we have a variant, the serviceId format is "serviceId-variantId"
            // We need to remove the variant ID from the end
            const variantId = variant.id;
            if (serviceId.endsWith(`-${variantId}`)) {
              originalServiceId = serviceId.slice(0, -(variantId.length + 1)); // +1 for the dash
            }
          }

          const service = services.find((s) => s.id === originalServiceId);

          if (service) {
            // Create service with variant information
            const serviceWithVariant = {
              ...service,
              id: serviceId, // Use the unique ID (could be original or variant-specific)
              selected: true,
              quantity: 1,
              cartQuantity: 1,
              // Store variant information if selected
              ...(variant && {
                variantId: variant.id,
                variantName: variant.name,
                variantPrice: variant.price,
                variantDuration: variant.duration,
                // Override service properties with variant properties
                price: variant.price,
                duration: variant.duration,
                name: variant.name,
                description: variant.description || service.description,
              }),
            };

            return [...prev, serviceWithVariant];
          } else {
            return prev;
          }
        }
      });

      // Update formatted services
      setFormattedServices((prev) =>
        prev.map((category) => ({
          ...category,
          services: category.services.map((service) =>
            service.id === serviceId
              ? { ...service, selected: !service.selected }
              : service
          ),
        }))
      );
    },
    [giftCardCart, selectedPackages, services, addToast]
  );

  const deleteService = useCallback((serviceId: string) => {
    setSelectedServices((prev) => prev.filter((s) => s.id !== serviceId));
    setFormattedServices((prev) =>
      prev.map((category) => ({
        ...category,
        services: category.services.map((service) =>
          service.id === serviceId ? { ...service, selected: false } : service
        ),
      }))
    );
  }, []);

  const clearServicesInCart = useCallback(() => {
    setSelectedServices([]);
    setFormattedServices((prev) =>
      prev.map((category) => ({
        ...category,
        services: category.services.map((service) => ({
          ...service,
          selected: false,
        })),
      }))
    );
  }, []);

  // Package Management
  const modifyPackageInCart = useCallback(
    (packageId: string, action: "add" | "remove" | "clear") => {
      if (action === "clear") {
        setSelectedPackages([]);
        return;
      }

      if (giftCardCart) {
        addToast({
          message: "Please clear gift cards from cart before adding packages",
          variant: "error",
        });
        return;
      }

      if (selectedServices.length > 0) {
        addToast({
          message: "Please clear services from cart before adding packages",
          variant: "error",
        });
        return;
      }

      setSelectedPackages((prev) => {
        const existingPackage = prev.find((p) => p.id === packageId);

        if (action === "add") {
          if (existingPackage) {
            return prev.map((p) =>
              p.id === packageId ? { ...p, quantity: p.quantity + 1 } : p
            );
          } else {
            const pkg = formattedPackages.find((p) => p.id === packageId);
            if (pkg) {
              return [...prev, { ...pkg, quantity: 1, selected: true }];
            }
          }
        } else if (action === "remove") {
          if (existingPackage) {
            if (existingPackage.quantity > 1) {
              return prev.map((p) =>
                p.id === packageId ? { ...p, quantity: p.quantity - 1 } : p
              );
            } else {
              return prev.filter((p) => p.id !== packageId);
            }
          }
        }

        return prev;
      });
    },
    [giftCardCart, selectedServices, formattedPackages, addToast]
  );

  const modifyServiceInCart = useCallback(
    (serviceId: string, action: "add" | "remove" | "clear") => {
      if (action === "clear") {
        clearServicesInCart();
        return;
      }

      if (giftCardCart) {
        addToast({
          message: "Please clear gift cards from cart before modifying services",
          variant: "error",
        });
        return;
      }

      if (selectedPackages.length > 0) {
        addToast({
          message: "Please clear packages from cart before modifying services",
          variant: "error",
        });
        return;
      }

      setSelectedServices((prev) => {
        const existingService = prev.find((s) => s.id === serviceId);

        if (action === "add") {
          if (existingService) {
            return prev.map((s) =>
              s.id === serviceId ? { ...s, quantity: (s.quantity || 1) + 1 } : s
            );
          }
        } else if (action === "remove") {
          if (existingService) {
            if ((existingService.quantity || 1) > 1) {
              return prev.map((s) =>
                s.id === serviceId
                  ? { ...s, quantity: (s.quantity || 1) - 1 }
                  : s
              );
            } else {
              return prev.filter((s) => s.id !== serviceId);
            }
          }
        }

        return prev;
      });
    },
    [giftCardCart, selectedPackages, clearServicesInCart, addToast]
  );

  // Search Management
  const closeSearch = useCallback(() => {
    setDebouncedSearchQuery("");
    setSearchWrapperIsOpen(false);
    setSearchCount(0);
    if (services.length > 0) {
      setFormattedServices(formatServicesIntoGroups(services));
    }
  }, [services]);

  const openSearchWrapper = useCallback((box?: boolean) => {
    setSearchWrapperIsOpen(true);
  }, []);

  const resetBooking = useCallback(() => {
    setStep(BookingStep.INITIAL);
    setSelectedServices([]);
    setSelectedPackages([]);
    setGiftCardCartState(null);
    setVoucherToBeRedeemed(null);
    setSelectedStaff(null);
    setAppointmentDateTime(null);
    setServiceClient(null);
    setPriceSummary(null);
    setPromoCode(null);
    setPromoCodeApplication(null);
    setBookedAppointment(null);
    setActualAmountPaid(null);
    setActualProcessingFee(null);
    setSelectedReward(null);
    setRewardSelected(false);
    // Reset workflow form state
    setWorkflowForm(null);
    setWorkflowFormAnswers({});
    setShowWorkflowFormModal(false);
    setWorkflowFormSubmissionId(null);
    setSelectedDate(null);
    setSelectedTime(null);
    setSlotSelectionTime(null);
  }, []);

  // Workflow Form Actions
  const updateWorkflowFormAnswer = useCallback(
    (fieldId: string, value: string | string[] | boolean) => {
      setWorkflowFormAnswers((prev) => ({
        ...prev,
        [fieldId]: value,
      }));
    },
    []
  );

  const clearWorkflowForm = useCallback(() => {
    setWorkflowForm(null);
    setWorkflowFormAnswers({});
    setShowWorkflowFormModal(false);
    setWorkflowFormSubmissionId(null);
  }, []);

  // Clear selected date/time (for availability polling reset)
  const clearSelectedDateTime = useCallback(() => {
    setSelectedDate(null);
    setSelectedTime(null);
    setSlotSelectionTime(null);
  }, []);

  const value: BookingContextType = {
    // State
    step,
    services,
    formattedServices,
    locationType,
    formattedPackages,
    selectedServices,
    selectedPackages,
    giftCardCart,
    selectedStaff,
    availableServiceStaffs,
    businessInfo,
    salons,
    selectedSalon,
    selectedSalonId,
    appointmentDateTime,
    priceSummary,
    packagesPriceSummary,
    serviceClient,
    voucherToBeRedeemed,
    promoCodeApplication,
    promoCode,
    rewards,
    selectedReward,
    activeTab,
    openVoucherRedemptionModal,
    bookedAppointment,
    appointmentPaymentType,
    actualAmountPaid,
    actualProcessingFee,
    staffOption,
    debouncedSearchQuery,
    searchCount,
    searchWrapperIsOpen,
    rewardSelected,
    faqs,
    reviews,
    isMobile,
    existingAppointment,
    loadingServices,
    promos,
    // Workflow Form State
    workflowForm,
    workflowFormAnswers,
    showWorkflowFormModal,
    workflowFormSubmissionId,
    selectedDate,
    selectedTime,
    slotSelectionTime,

    // Actions
    handleNextStep,
    setLocationType,
    setFormattedServices,
    showOrHideCategory,
    selectService,
    selectServiceWithVariant,
    setSelectedServices,
    setSelectedSalonId,
    setAppointmentDateTime,
    setServiceClient,
    setActiveTab,
    setPriceSummary,
    clearServicesInCart,
    setVoucherToBeRedeemed,
    setOpenVoucherRedemptionModal,
    setBookedAppointment,
    setPromoCodeApplication,
    setPromoCode,
    setPackagesPriceSummary,
    setAppointmentPaymentType,
    setActualAmountPaid,
    setActualProcessingFee,

    setStaffOption,
    setRewards,
    setSelectedReward,
    closeSearch,
    openSearchWrapper,
    setDebouncedSearchQuery,
    setRewardSelected,
    deleteService,
    setSelectedStaff,
    setAvailableServiceStaffs,
    modifyPackageInCart,
    modifyServiceInCart,
    setGiftCardCart,
    updateGiftCardCartQuantity,
    clearGiftCardCart,
    addToast,
    scrollToTop,
    resetBooking,
    // Workflow Form Actions
    setWorkflowForm,
    setWorkflowFormAnswers,
    setShowWorkflowFormModal,
    setWorkflowFormSubmissionId,
    updateWorkflowFormAnswer,
    clearWorkflowForm,
    setSelectedDate,
    setSelectedTime,
    clearSelectedDateTime,
    setSlotSelectionTime,
    deleteHeldAppointment,
  };

  return (
    <BookingContext.Provider value={value}>
      <div ref={modalContentRef}>{children}</div>
    </BookingContext.Provider>
  );
}

export default BookingContext;
